import * as yup from "yup";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, IconButton, TextField } from "@mui/material";
import { mdiAccountPlus, mdiEye, mdiEyeOff } from "@mdi/js";
import Icon from "@mdi/react";

import AuthApi from "api/auth";
import AppBar from "components/AppBar";
import AuthForm from "components/AuthForm";
import GdprConsent from "components/GdprConsent";
import PageLayout from "components/PageLayout";
import useNotifier from "hooks/useNotifier";

const validationSchema = yup.object().shape({
  email: yup.string().trim().required("Email address is required").email("Email address is not valid"),
  password: yup.string().required("Password is required").min(8, "Password must be longer than 8 characters"),
  confirmedPassword: yup
    .string()
    .required("Confirm password must match")
    .oneOf([yup.ref("password")], "Passwords must match"),
});

function SignUp() {
  const [showPassword, setShowPassword] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const navigate = useNavigate();
  const { showNotification } = useNotifier();
  const authApi = useMemo(() => new AuthApi(showNotification), [showNotification]);

  const signUp = async (data: { email: string; password: string }) => {
    const { error } = await authApi.signUp(data.email, data.password);

    if (!error) {
      navigate("/");
    }
  };

  return (
    <PageLayout appBar={<AppBar backAction={false} noRadius showLogo title='GroupUp' />}>
      <AuthForm<SignUpForm>
        avatarIcon={mdiAccountPlus}
        links={[{ label: "Already have an account? Sign in", href: "/" }]}
        submitDisabled={!termsAccepted}
        submitLabel='Sign Up'
        title='Sign Up'
        validationSchema={validationSchema}
        notInputChildren={[3]}
        onSubmit={signUp}
      >
        <TextField name='email' label='Email address' autoComplete='email' fullWidth autoFocus />
        <TextField
          fullWidth
          InputProps={{
            endAdornment: (
              <IconButton
                color='primary'
                aria-label='toggle password visibility'
                onClick={() => setShowPassword(!showPassword)}
                edge='end'
              >
                {showPassword ? <Icon path={mdiEye} size={1} /> : <Icon path={mdiEyeOff} size={1} />}
              </IconButton>
            ),
          }}
          label='Password'
          name='password'
          type={showPassword ? "text" : "password"}
        />
        <TextField
          id='confirmed-password'
          fullWidth
          InputProps={{
            endAdornment: (
              <IconButton
                color='primary'
                aria-label='toggle password visibility'
                onClick={() => setShowPassword(!showPassword)}
                edge='end'
              >
                {showPassword ? <Icon path={mdiEye} size={1} /> : <Icon path={mdiEyeOff} size={1} />}
              </IconButton>
            ),
          }}
          label='Confirm password'
          name='confirmedPassword'
          type={showPassword ? "text" : "password"}
        />

        <Box sx={{ mt: 1 }}>
          <GdprConsent checked={termsAccepted} onCheck={() => setTermsAccepted(!termsAccepted)} />
        </Box>
      </AuthForm>
    </PageLayout>
  );
}

export default SignUp;

interface SignUpForm extends Record<string, unknown> {
  email: string;
  password: string;
  confirmedPassword: string;
}
